import { pathOr } from 'ramda';
import { I18n } from 'src/translations/i18n';
import FormSelect from 'src/components/Form/FormSelect';
import FormInput from 'src/components/Form/FormInput';
import FormTimePicker from 'src/components/Form/FormTimePicker';
import FormCheckbox from 'src/components/Form/FormCheckbox';
import { arrayRequired, numberFrom, numberTo, required } from 'src/components/Form/validations';
// save validations to have same links and avoid unnecessary re-renders
const from1 = numberFrom(1);
const to100 = numberTo(100);
const to12 = numberTo(12);
const to7 = numberTo(7);
const to48 = numberTo(48);
const to70 = numberTo(70);
const to24 = numberTo(24);
const to120 = numberTo(120);
export const getParagraphLabelById = (id) => {
    switch (id) {
        case 1:
            return '§ 47.';
        case 2:
            return '§ 51.';
        case 3:
            return '§ 46.';
        case 4:
            return '§ 47.';
        case 5:
            return '§ 51.';
        case 6:
            return '§ 52.';
        default:
            return '';
    }
};
export var ParagraphKeys;
(function (ParagraphKeys) {
    // Internal names to names used by API
    ParagraphKeys["BreakTimesForRestAndMealParagraph"] = "BreakTimesForRestAndMealParagraph";
    ParagraphKeys["BreakTimesParagraph"] = "BreakTimesParagraph";
    ParagraphKeys["RestBetweenWorkingDaysAndWorkShifts"] = "RestBetweenWorkingDaysAndWorkShifts";
    ParagraphKeys["LimitationOfTheTimeOfWork"] = "LimitationOfTheTimeOfWork";
    ParagraphKeys["OrganizationOfWorkingTime"] = "OrganizationOfWorkingTime";
    ParagraphKeys["DailyRestPeriod"] = "DailyRestPeriod";
    ParagraphKeys["WeeklyRestPeriod"] = "WeeklyRestPeriod";
})(ParagraphKeys || (ParagraphKeys = {}));
export const paragraphDescriptionKeys = {
    [ParagraphKeys.BreakTimesForRestAndMealParagraph]: 'LawWorkPr_16Description',
    [ParagraphKeys.BreakTimesParagraph]: 'BreakTimesDescription',
    [ParagraphKeys.RestBetweenWorkingDaysAndWorkShifts]: 'LawWorkPr_20Description',
    [ParagraphKeys.LimitationOfTheTimeOfWork]: 'LawEmpContPr_46Description',
    [ParagraphKeys.OrganizationOfWorkingTime]: 'LawEmpContPr_47Description',
    [ParagraphKeys.DailyRestPeriod]: 'LawEmpContPr_51Description',
    [ParagraphKeys.WeeklyRestPeriod]: 'LawEmpContPr_52Description'
};
export const lawCheckParagraphDescriptionKeys = {
    [ParagraphKeys.BreakTimesForRestAndMealParagraph]: 'LawEmpContPr_47Description',
    [ParagraphKeys.LimitationOfTheTimeOfWork]: 'LawEmpContPr_46Description',
    [ParagraphKeys.DailyRestPeriod]: 'LawEmpContPr_51Description',
    [ParagraphKeys.WeeklyRestPeriod]: 'LawEmpContPr_52Description'
};
export const getParagraphOptions = () => [{
        name: ParagraphKeys.LimitationOfTheTimeOfWork,
        label: I18n.t('LimitationOfTheTimeOfWork')
    }, {
        name: ParagraphKeys.BreakTimesForRestAndMealParagraph,
        label: I18n.t('OrganizationOfWorkingTime')
    }, {
        name: ParagraphKeys.DailyRestPeriod,
        label: I18n.t('DailyRestPeriod')
    }, {
        name: ParagraphKeys.WeeklyRestPeriod,
        label: I18n.t('WeeklyRestPeriod')
    }];
export const getFormInitials = (paragraphName) => {
    const initials = {
        [ParagraphKeys.RestBetweenWorkingDaysAndWorkShifts]: {
            configuration: {
                MinutesBetweenEvents: 11
            }
        },
        [ParagraphKeys.BreakTimesForRestAndMealParagraph]: {
            configuration: {
                LunchStart: '4:00'
            }
        },
        [ParagraphKeys.LimitationOfTheTimeOfWork]: {
            configuration: {
                AccountingPeriod: 4,
                Interval: 7,
                AllowedWorkTime: 48
            }
        },
        [ParagraphKeys.OrganizationOfWorkingTime]: {
            configuration: {
                RegimeLength: 6,
                BreakLength: 30
            }
        },
        [ParagraphKeys.DailyRestPeriod]: {
            configuration: {
                VerifiableTime: 24,
                RestTimeLength: 11
            }
        },
        [ParagraphKeys.WeeklyRestPeriod]: {
            configuration: {
                VerifiablePeriod: 7,
                RestTimeLength: 48
            }
        }
    };
    return pathOr({}, [paragraphName], initials);
};
export const getFieldsConfig = (paragraphName) => {
    const ContractTypeField = {
        name: 'ContractTypeIds',
        mouseInKey: 'Law-ContractTypeIds',
        component: FormSelect,
        title: I18n.t('RelatedTo'),
        optionsPath: ['contractTypes'],
        translateOptions: true,
        isRequired: true
    };
    const TimeRateSystemField = {
        name: 'TimeRateSystemIds',
        mouseInKey: 'Law-TimeRateSystem',
        component: FormSelect,
        title: I18n.t('TimeRateSystem'),
        optionsPath: ['timeRateSystems'],
        translateOptions: true
    };
    const RestTimeLengthField = {
        name: 'RestTimeLength',
        component: FormInput,
        title: I18n.t('RestTimeLength'),
        postfix: I18n.t('Hours'),
        type: 'number',
        isRequired: true,
        errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 70 }),
        validate: [required, from1, to70]
    };
    const configs = {
        [ParagraphKeys.BreakTimesForRestAndMealParagraph]: [
            ContractTypeField,
            {
                name: 'LunchLength',
                mouseInKey: 'Law-LunchLength',
                component: FormInput,
                title: I18n.t('LunchLength'),
                postfix: I18n.t('min'),
                type: 'number',
                isRequired: true,
                validate: [required]
            }, {
                name: 'AllowedDeviation',
                mouseInKey: 'Law-AllowedDeviation',
                component: FormInput,
                title: I18n.t('Buffer'),
                postfix: I18n.t('min'),
                type: 'number',
                isRequired: true,
                validate: [required]
            }, {
                name: 'LunchStart',
                mouseInKey: 'Law-LunchStart',
                component: FormTimePicker,
                title: I18n.t('LunchStart'),
                postfix: I18n.t('HoursAfterFirstWorkStart'),
                placeholder: '00:00',
                isRequired: true,
                validate: [required]
            }, {
                name: 'LongLunchesActivated',
                mouseInKey: 'Law-LongLunchActive',
                component: FormCheckbox,
                title: I18n.t('LongLunchActive')
            }, {
                name: 'LongLunchLength',
                mouseInKey: 'Law-LongLunchLength',
                component: FormInput,
                title: I18n.t('LongLunchLength'),
                type: 'number',
                formDependencies: [
                    {
                        fieldPath: 'configuration.LongLunchesActivated',
                        fieldValue: true,
                        propsToAdd: {
                            isRequired: true,
                            validate: [required, from1]
                        }
                    }
                ]
            }, {
                name: 'MinimumLongWorkTime',
                mouseInKey: 'Law-MinimumLongWorkTime',
                title: I18n.t('MinimumLongWorkTime'),
                postfix: I18n.t('min'),
                component: FormTimePicker,
                placeholder: '00:00',
                formDependencies: [
                    {
                        fieldPath: 'configuration.LongLunchesActivated',
                        fieldValue: true,
                        propsToAdd: {
                            isRequired: true,
                            validate: [required]
                        }
                    }
                ]
            }
        ],
        [ParagraphKeys.BreakTimesParagraph]: [
            ContractTypeField,
            {
                name: 'BreakLength',
                mouseInKey: 'Law-PauseLength',
                component: FormInput,
                title: I18n.t('PauseLength'),
                postfix: I18n.t('min'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('FieldIsRequired'),
                validate: [required]
            }, {
                name: 'AllowedDeviation',
                mouseInKey: 'Law-AllowedDeviation',
                component: FormInput,
                title: I18n.t('Buffer'),
                postfix: I18n.t('min'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('FieldIsRequired'),
                validate: [required]
            }, {
                name: 'BreakStart',
                mouseInKey: 'Law-PauseStart',
                component: FormTimePicker,
                title: I18n.t('PauseStart'),
                postfix: I18n.t('HoursAfterFirstWorkStart'),
                placeholder: '00:00',
                isRequired: true,
                errorMessage: I18n.t('FieldIsRequired'),
                validate: [required]
            }
        ],
        [ParagraphKeys.RestBetweenWorkingDaysAndWorkShifts]: [
            ContractTypeField,
            {
                name: 'MinutesBetweenEvents',
                mouseInKey: 'Law-MinutesBetweenEvents',
                component: FormInput,
                title: I18n.t('Hours'),
                type: 'number',
                defaultValue: 11,
                placeholder: '11',
                postfix: I18n.t('Hours'),
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 100 }),
                validate: [required, from1, to100]
            }
        ],
        [ParagraphKeys.LimitationOfTheTimeOfWork]: [
            ContractTypeField,
            TimeRateSystemField,
            {
                name: 'AccountingPeriod',
                component: FormInput,
                title: I18n.t('AccountingPeriod'),
                postfix: I18n.t('Months'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 12 }),
                validate: [required, from1, to12]
            },
            {
                name: 'Interval',
                component: FormInput,
                title: I18n.t('Interval'),
                postfix: I18n.t('Days'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 7 }),
                validate: [required, from1, to7]
            },
            {
                name: 'AllowedWorkTime',
                component: FormInput,
                title: I18n.t('AllowedWorkTime'),
                postfix: I18n.t('Hours'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 48 }),
                validate: [required, from1, to48]
            }
        ],
        [ParagraphKeys.OrganizationOfWorkingTime]: [
            ContractTypeField,
            {
                name: 'BreakIds',
                mouseInKey: 'Law-LunchJobId',
                component: FormSelect,
                title: I18n.t('LunchJobId'),
                optionsPath: ['jobs'],
                validate: [arrayRequired],
                isRequired: true,
                errorMessage: I18n.t('FieldIsRequired')
            },
            {
                name: 'RegimeLength',
                component: FormInput,
                title: I18n.t('VerifiableTime'),
                postfix: I18n.t('Hours'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 24 }),
                validate: [required, from1, to24]
            },
            {
                name: 'BreakLength',
                component: FormInput,
                title: I18n.t('LunchLength'),
                postfix: I18n.t('min'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 120 }),
                validate: [required, from1, to120]
            }
        ],
        [ParagraphKeys.DailyRestPeriod]: [
            ContractTypeField,
            {
                name: 'VerifiableTime',
                component: FormInput,
                title: I18n.t('VerifiableTime'),
                postfix: I18n.t('Hours'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 24 }),
                validate: [required, from1, to24]
            },
            RestTimeLengthField
        ],
        [ParagraphKeys.WeeklyRestPeriod]: [
            ContractTypeField,
            TimeRateSystemField,
            {
                name: 'VerifiablePeriod',
                component: FormInput,
                title: I18n.t('VerifiablePeriod'),
                postfix: I18n.t('Days'),
                type: 'number',
                isRequired: true,
                errorMessage: I18n.t('LengthMinMaxCharacters', { min: 1, max: 7 }),
                validate: [required, from1, to48],
                disabled: true
            },
            RestTimeLengthField
        ]
    };
    return pathOr([], [paragraphName], configs);
};
